import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, Signal, X } from "lucide-react";
import { Reveal, SectionHeader, Corners } from "./Bits";

const SOLDIERS_IMG =
  "https://images.unsplash.com/photo-1569242840510-9fe6f0112cee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHx0YWN0aWNhbCUyMG1pbGl0YXJ5JTIwZ2VhcnxlbnwwfHx8fDE3ODY5OTQ5MDJ8MA&ixlib=rb-4.1.0&q=85";

const TRAILER_URL = "https://assets.mixkit.co/videos/23986/23986-720.mp4";

export const Transmission = () => {
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
  }, [open]);

  return (
    <section
      id="transmission"
      data-testid="transmission-section"
      className="border-t border-line px-6 py-28 md:px-12 md:py-36"
    >
      <div className="mx-auto max-w-[1400px]">
        <SectionHeader
          index="05"
          label="TRANSMISSION"
          title="Intercepted Broadcast"
          desc="Recovered footage from a field unit's helmet feed. Decryption is partial — what survives is enough."
        />
        <Reveal>
          <button
            data-testid="play-transmission-btn"
            onClick={() => setOpen(true)}
            className="clip-angle group relative block h-[320px] w-full overflow-hidden border border-line text-left md:h-[460px]"
          >
            <img
              src={SOLDIERS_IMG}
              alt="Helmet-cam still from an intercepted field transmission"
              className="h-full w-full object-cover brightness-[0.4] contrast-125 saturate-0 transition-[filter] duration-300 group-hover:brightness-[0.55]"
            />
            <div className="grid-bg absolute inset-0 opacity-20" />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-5">
              <span className="relative flex h-16 w-16 items-center justify-center border border-signal">
                <span className="absolute inset-0 animate-ping border border-signal/60" />
                <Play className="h-5 w-5 text-signal" />
              </span>
              <span className="font-mono text-[11px] font-bold tracking-[0.35em] text-signal">
                PLAY TRANSMISSION
              </span>
            </div>
            <span className="absolute left-4 top-4 font-mono text-[10px] tracking-[0.3em] text-slate-400">
              REC <span className="text-signal">●</span> 04:00:12
            </span>
            <span className="absolute bottom-4 right-4 font-mono text-[10px] tracking-[0.3em] text-slate-500">
              RUNTIME: 01:47 // CLASSIFIED
            </span>
            <Corners className="border-signal/60" />
          </button>
        </Reveal>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="trailer-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.98, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="w-full max-w-5xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-3 flex items-center justify-between font-mono text-[10px] tracking-[0.3em] text-slate-500">
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 animate-pulse bg-signal" />
                  INCOMING TRANSMISSION // DECRYPTING…
                </span>
                <button
                  data-testid="trailer-close-btn"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 text-signal transition-colors hover:text-[#F8FAFC]"
                >
                  <X className="h-4 w-4" /> CLOSE
                </button>
              </div>
              <div className="glitch-frame clip-angle relative aspect-video overflow-hidden border border-signal/40 bg-black">
                <Corners />
                {failed ? (
                  <div
                    data-testid="trailer-fallback"
                    className="flex h-full w-full flex-col items-center justify-center gap-4 bg-void"
                  >
                    <Signal className="h-8 w-8 animate-pulse text-signal" />
                    <p className="font-mono text-xs font-bold tracking-[0.3em] text-signal">
                      SIGNAL LOST // TRANSMISSION SEALED
                    </p>
                    <p className="font-mono text-[10px] tracking-[0.25em] text-slate-600">
                      FULL DECRYPTION SHIPS WITH LAUNCH BUILD
                    </p>
                  </div>
                ) : (
                  <video
                    data-testid="trailer-video"
                    src={TRAILER_URL}
                    autoPlay
                    controls
                    playsInline
                    className="h-full w-full object-cover"
                    onError={() => setFailed(true)}
                  />
                )}
              </div>
              <p className="mt-3 font-mono text-[10px] tracking-[0.3em] text-slate-600">
                REC 04:00:12 // SOURCE: <span className="redacted">FIELD UNIT 7</span> // ESC TO
                SEAL
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
